/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  downloadFile,
  Sidebar,
  TextoConFormato,
  tituloBase,
  TablaFiltrable
} from "../../shared";
import { Titulo } from "../../shared";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Column } from "primereact/column";
import { Formulario } from "../../shared";
import { Toast } from "primereact/toast";
import { Boton } from "../../shared";
import { CampoInformacion, InfoCertificadoPropuesta } from "../../shared";
import { fechaCorta } from "../../portal/utilidades";

const FRECUENCIA_LABEL = {
  SEMANAL: "Semanal",
  MENSUAL: "Mensual",
  SEMESTRAL: "Semestral",
  ANUAL: "Anual",
  BIENAL: "Bienal",
  TRIENAL: "Trienal"
};

export default function PropuestaComercial() {
  const [propuesta, setPropuesta] = useState(null);
  // Título de la pestaña, como en el admin: el de este registro cuando carga
  useEffect(() => {
    if (propuesta) document.title = propuesta.solicitud?.nombrefantasia || "Propuesta comercial";
  }, [propuesta]);
  useEffect(() => () => (document.title = tituloBase()), []);
  const [loader, setLoader] = useState(true);
  const [monedas, setMonedas] = useState();
  const [loaderAceptacion, setLoaderAceptacion] = useState(false);
  const [datosAceptacion, setDatosAceptacion] = useState({});
  const [sidebarVisible, setSidebarVisible] = useState(false);

  const toast = useRef(null);
  const { propuestaId } = useParams();

  const [descargandoPDF, setDescargandoPDF] = useState(false);

  const descargarPDF = () => {
    setDescargandoPDF(true);
    downloadFile(
      `cliente/propuesta/${propuestaId}/pdf`,
      `Propuesta comercial - ${propuesta.solicitud?.nombrefantasia}.pdf`,
      () => {
        setDescargandoPDF(false);
      },
      (error) => {
        setDescargandoPDF(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              error[0]?.msg ||
              "No se pudo descargar el PDF. Intente nuevamente más tarde.",
            sticky: true
          });
        }
      }
    );
  };

  // Cuando carga el componente trae los detalles de la propuesta comercial
  // y las monedas
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (propuestaId) {
      accessAPI(
        "GET",
        `cliente/propuesta/${propuestaId}`,
        null,
        (data) => {
          setPropuesta(data);
        },
        (respuesta) => {
          if (toast.current) {
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail:
                respuesta[0]?.msg ||
                "Ocurrió un error al cargar la propuesta. Intente nuevamente más tarde.",
              sticky: true
            });
          }
        }
      );
    }

    accessAPI(
      "GET",
      "cliente/config/monedas",
      null,
      (respuesta) => {
        setMonedas(respuesta);
      },
      (respuesta) => {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              respuesta[0]?.msg ||
              "Ocurrió un error al cargar las monedas. Intente nuevamente más tarde.",
            sticky: true
          });
        }
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (propuesta && monedas) {
      // Si la propuesta fue aceptada, carga los datos de aceptacion
      if (propuesta.fechaaceptacion) {
        setDatosAceptacion({
          fechaaceptacion: propuesta.fechaaceptacion,
          nombreAceptador: propuesta.nombreAceptador
        });
      }
      setLoader(false);
    }
  }, [propuesta, monedas]);

  function responderPropuesta(aceptacion, camposFormulario) {
    setLoaderAceptacion(true);
    accessAPI(
      "PUT",
      `cliente/propuesta/aceptar/${propuesta.id}`,
      { ...camposFormulario, aceptacion },
      (respuesta) => {
        if (aceptacion) {
          setDatosAceptacion(respuesta);
          if (toast.current) {
            toast.current.show({
              severity: "success",
              summary: "Éxito",
              detail: "La propuesta comercial ha sido aceptada exitosamente.",
              life: 3000
            });
          }
        } else {
          setDatosAceptacion({ noAceptada: true });
          console.log(respuesta[0].msg);
        }
        setLoaderAceptacion(false);
        setSidebarVisible(false);
      },
      (respuesta) => {
        setLoaderAceptacion(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              respuesta[0]?.msg ||
              "Ocurrió un error al responder la propuesta. Intente nuevamente más tarde.",
            sticky: true
          });
        }
      }
    );
  }

  const formularioAceptacionConfig = {
    secciones: [
      {
        descripcion:
          "Debajo podrá aceptar o rechazar la presente propuesta comercial. En caso de aceptarla, se disparará la facturación correspondiente. En caso de rechazarla, por favor póngase en contacto con nosotros para hacernos saber la razón del rechazo. En caso de que aplique se volverá a emitir la propuesta comercial con las correcciones pertinentes.",
        campos: [
          {
            etiqueta:
              "Comprendo que esta pantalla representa una propuesta comercial. Al aceptarla se pueden disparar procesos de facturación si corresponden.",
            nombre: "aceptacion1",
            requerido: true,
            tipoitemformularioid: 6
          },
          {
            etiqueta:
              "Leí y acepto las condiciones planteadas en los documentos de certificación.",
            nombre: "aceptacion2",
            requerido: true,
            tipoitemformularioid: 6
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Aceptar propuesta",
        validar: true,
        confirmacion: "¿Confirma la decisión de aceptar la propuesta?",
        callback: (camposFormulario) =>
          responderPropuesta(true, camposFormulario)
      },
      {
        etiqueta: "Rechazar propuesta",
        transparente: true,
        validar: true,
        confirmacion: "¿Confirma la decisicón de rechazar la propuesta?",
        callback: (camposFormulario) =>
          responderPropuesta(false, camposFormulario)
      }
    ]
  };

  return (
    <main>
      <Toast ref={toast} position="top-center" />
      {loader && (
        <>
          <Titulo urlVolver="/propuestas">
            <Skeleton width="300px" height="2rem" />
          </Titulo>
          <Card css={{ marginBottom: "1em" }}>
            <div
              css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} width="100%" height="1rem" />
              ))}
            </div>
          </Card>
          <Card css={{ marginBottom: "1em" }}>
            <div className="grid">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="col-6" css={{ marginBottom: "1rem" }}>
                  <Skeleton
                    width="100%"
                    height="1rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.5rem" />
                </div>
              ))}
            </div>
          </Card>
          <Card css={{ marginBottom: "1em" }}>
            <div
              css={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              {[...Array(3)].map((_, i) => (
                <div key={i}>
                  <Skeleton width="100%" height="4rem" />
                </div>
              ))}
            </div>
          </Card>
          <Card css={{ marginBottom: "1em" }}>
            <Skeleton width="60%" height="1.5rem" />
          </Card>
          <Card css={{ marginBottom: "1em" }}>
            <div
              css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} width="100%" height="1rem" />
              ))}
            </div>
          </Card>
        </>
      )}
      {!loader && propuesta && monedas && (
        <div>
          <Titulo
            subtitulo={propuesta.fechaemision && `Emitida el ${fechaCorta(propuesta.fechaemision)}`}
            urlVolver="/propuestas"
            etiqueta={
              datosAceptacion?.fechaaceptacion
                ? `Aceptada el ${fechaCorta(datosAceptacion.fechaaceptacion)}`
                : datosAceptacion &&
                    !datosAceptacion?.noAceptada &&
                    !datosAceptacion?.fechaaceptacion
                  ? "Pendiente de aceptación"
                  : undefined
            }
            ok={!!datosAceptacion?.fechaaceptacion}
            alerta={
              datosAceptacion &&
              !datosAceptacion?.noAceptada &&
              !datosAceptacion?.fechaaceptacion
            }
            botones={
              <>
                <Boton
                  callback={descargarPDF}
                  transparente
                  loader={descargandoPDF}
                >
                  Descargar PDF
                </Boton>
                {datosAceptacion &&
                  !datosAceptacion?.noAceptada &&
                  !datosAceptacion.fechaaceptacion && (
                    <Boton callback={() => setSidebarVisible(true)}>
                      Responder
                    </Boton>
                  )}
              </>
            }
          >
            {propuesta.solicitud?.nombrefantasia
              ? `Propuesta comercial - ${propuesta.solicitud.nombrefantasia}`
              : "Propuesta comercial de certificación"}
          </Titulo>
          {datosAceptacion?.fechaaceptacion && (
            <Card
              css={{
                marginBottom: "1em",
                backgroundColor: "var(--green-50)",
                border: "1px solid var(--green-200)"
              }}
            >
              <div
                css={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
              >
                <i
                  className="pi pi-check-circle"
                  css={{ fontSize: "1.5rem", color: "var(--green-600)" }}
                />
                <div>
                  <div css={{ fontWeight: "bold", color: "var(--green-700)" }}>
                    Propuesta aceptada
                  </div>
                  <div css={{ color: "var(--green-600)" }}>
                    {datosAceptacion.nombreAceptador
                      ? `Aceptada por ${datosAceptacion.nombreAceptador} el `
                      : "Aceptada el "}
                    {fechaCorta(datosAceptacion.fechaaceptacion)}
                  </div>
                </div>
              </div>
            </Card>
          )}
          {propuesta.facturas?.map((factura) => {
            const pagada = !!factura.fechaPago;
            return (
              <Card
                key={factura.id}
                css={{
                  marginBottom: "1em",
                  backgroundColor: pagada
                    ? "var(--green-50)"
                    : "var(--yellow-50)",
                  border: `1px solid ${pagada ? "var(--green-200)" : "var(--yellow-200)"}`,
                  cursor: factura.idFacturaBiller ? "pointer" : "default"
                }}
                onClick={() => {
                  if (!factura.idFacturaBiller) return;
                  downloadFile(
                    `cliente/destacados/factura/${factura.id}/pdf`,
                    `Factura-${factura.numeroFactura || factura.id}.pdf`,
                    () => {},
                    () => {}
                  );
                }}
              >
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem"
                  }}
                >
                  <i
                    className={pagada ? "pi pi-check-circle" : "pi pi-clock"}
                    css={{
                      fontSize: "1.5rem",
                      color: pagada ? "var(--green-600)" : "var(--yellow-600)"
                    }}
                  />
                  <div>
                    {pagada ? (
                      <>
                        <div
                          css={{
                            fontWeight: "bold",
                            color: "var(--green-700)"
                          }}
                        >
                          Factura pagada el {fechaCorta(factura.fechaPago)}
                        </div>
                        {factura.idFacturaBiller && (
                          <div
                            css={{
                              color: "var(--green-600)",
                              fontSize: "0.85em"
                            }}
                          >
                            Click aquí para descargar factura
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div
                          css={{
                            fontWeight: "bold",
                            color: "var(--yellow-700)"
                          }}
                        >
                          Factura pendiente de pago
                        </div>
                        {factura.idFacturaBiller && (
                          <div
                            css={{
                              color: "var(--yellow-600)",
                              fontSize: "0.85em"
                            }}
                          >
                            Click aquí para descargar factura
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
          <Card css={{ marginBottom: "1em" }}>
            <Titulo>Importante, leer con atención</Titulo>
            <TextoConFormato texto={propuesta.textoPropuestaImportante} />
          </Card>
          <Card css={{ marginBottom: "1em" }}>
            <Titulo>Detalles</Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Cliente">
                {propuesta.cliente?.razonsocial}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Solicitud">
                {propuesta.solicitud?.nombrefantasia}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de emisión">
                {fechaCorta(propuesta.fechaemision)}
              </CampoInformacion>

              {propuesta.notas && (
                <CampoInformacion nombreCampo="Notas">
                  {propuesta.notas.split("\n").map((parrafo, index) => {
                    return (
                      <span key={index}>
                        {parrafo}
                        <br />
                      </span>
                    );
                  })}
                </CampoInformacion>
              )}
            </div>
          </Card>
          {(() => {
            const actividades = propuesta.precios?.actividades || [];
            const adicionales = propuesta.precios?.adicionales || [];

            // Agrupa las actividades por tipo de certificado, preservando el
            // orden de aparición.
            const grupos = [];
            const indicePorTipo = {};
            for (const a of actividades) {
              const clave = a.certificadoTipoId ?? "sin";
              if (!(clave in indicePorTipo)) {
                indicePorTipo[clave] = grupos.length;
                grupos.push({
                  clave,
                  // Título de sección: {tipoProceso} - {tipoCertificado}. En
                  // propuestas viejas sin procesotiponombre queda solo el
                  // certificado.
                  nombre: [a.procesotiponombre, a.certificadonombre]
                    .filter(Boolean)
                    .join(" - "),
                  notas: a.certificadoNotasPropuesta,
                  // Norma, esquema y reglamentación son del tipo de
                  // certificado, no de cada actividad.
                  certificado: (propuesta.precios?.certificados || []).find(
                    (c) => c.certificadoTipoId === a.certificadoTipoId
                  ),
                  actividades: []
                });
              }
              grupos[indicePorTipo[clave]].actividades.push(a);
            }

            const precioBody = (a) =>
              a.precio === 0
                ? "Bonificada"
                : `${a.moneda?.simbolo || ""} ${a.precio}${a.moneda?.nombre ? ` (${a.moneda.nombre})` : ""}`;
            const frecuenciaBody = (a) =>
              a.precio === 0
                ? ""
                : a.frecuenciaRefacturacion
                  ? FRECUENCIA_LABEL[a.frecuenciaRefacturacion] ||
                    a.frecuenciaRefacturacion
                  : "Por actividad";
            const primeraBody = (a) =>
              a.precio === 0
                ? ""
                : a.cantidad > 0
                  ? `${a.moneda?.simbolo || ""} ${a.subtotal}`
                  : "—";

            return (
              <>
                {propuesta.procesos?.length > 0 && (
                  <Card css={{ marginBottom: "1em" }}>
                    <Titulo>Procesos alcanzados</Titulo>
                    <ul css={{ margin: 0, paddingLeft: "1.2em" }}>
                      {propuesta.procesos.map((p) => (
                        <li key={p.id}>
                          {[p.nombre, p.nombrefantasia].filter(Boolean).join(" · ")}
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
                {grupos.length === 0 && (
                  <Card css={{ marginBottom: "1em" }}>
                    <div>No hay actividades en la propuesta</div>
                  </Card>
                )}
                {grupos.map((g) => (
                  <Card key={g.clave} css={{ marginBottom: "1em" }}>
                    <Titulo>{g.nombre || "Actividades"}</Titulo>
                    <InfoCertificadoPropuesta
                      certificado={g.certificado}
                      notas={g.notas}
                    />
                    <h4 css={{ marginTop: 0 }}>Precios por actividad</h4>
                    <TablaFiltrable conFiltros={false}
                      value={g.actividades}
                      size="small"
                      dataKey="precioActividadClienteId"
                      css={{
                        // Oculta la 4ª columna (Primera facturación) hasta 991px.
                        "@media (max-width: 991px)": {
                          "& th:nth-of-type(4), & td:nth-of-type(4)": {
                            display: "none"
                          }
                        }
                      }}
                    >
                      <Column header="Actividad" field="actividadnombre" />
                      <Column header="Precio unitario" body={precioBody} />
                      <Column header="Facturación" body={frecuenciaBody} />
                      <Column header="Primera facturación" body={primeraBody} />
                    </TablaFiltrable>
                  </Card>
                ))}
                {adicionales.length > 0 && (
                  <Card css={{ marginBottom: "1em" }}>
                    <Titulo>Adicionales</Titulo>
                    <TablaFiltrable conFiltros={false} value={adicionales} size="small" dataKey="id">
                      <Column header="Descripción" field="descripcion" />
                      <Column
                        header="Precio"
                        body={(a) =>
                          `${a.moneda?.simbolo || ""} ${a.precio}${a.moneda?.nombre ? ` (${a.moneda.nombre})` : ""}`
                        }
                      />
                    </TablaFiltrable>
                  </Card>
                )}
              </>
            );
          })()}
          <Card css={{ marginBottom: "1em" }}>
            <Titulo>Facturación inicial</Titulo>
            <p>
              Esta facturación corresponde a las actividades que se crearán al
              aceptar la propuesta comercial. Actividades sucesivas que se
              soliciten serán facturadas aparte de acuerdo a los precios
              indicados arriba.
            </p>
            {propuesta.precios.totales.map((total, index) => {
              return (
                <div key={index}>
                  <strong>{`${total.moneda.simbolo} ${total.total} (${total.moneda.nombre})`}</strong>
                  {propuesta.masiva && <span> más IVA</span>}
                </div>
              );
            })}
          </Card>
          <Card css={{ marginBottom: "1em" }}>
            <Titulo>Aceptación</Titulo>
            <TextoConFormato texto={propuesta.textoPropuestaAceptacion} />
          </Card>
          {datosAceptacion &&
            !datosAceptacion?.noAceptada &&
            !datosAceptacion.fechaaceptacion && (
              <div
                css={{
                  textAlign: "center",
                  marginTop: "2em",
                  marginBottom: "2em"
                }}
              >
                <Boton callback={() => setSidebarVisible(true)}>
                  Responder
                </Boton>
              </div>
            )}

          <Sidebar
            visible={sidebarVisible}
            onHide={() => setSidebarVisible(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Responder propuesta comercial</Titulo>
            <Formulario
              config={formularioAceptacionConfig}
              loader={loaderAceptacion}
            />
          </Sidebar>
        </div>
      )}
    </main>
  );
}
